import { get, writable } from 'svelte/store';
import Router, { push, link } from "svelte-spa-router";
import { auth } from "./auth.js";
import { ACTIVE_BRIEF_TYPE_UUID } from "./briefConstants.js";

/** @typedef {import('./apiTypes.js').ApiResponse} ApiResponse */
/** @typedef {import('./apiTypes.js').ApiState} ApiState */
/** @typedef {Record<string, string | number | boolean | null | undefined>} HttpParams */

/** @type {ApiState} */
const initialState = {
    token: localStorage.getItem('token'),
    lists: JSON.parse(localStorage.getItem('lists') ?? 'null'),
    cart: null,
    data: null,
    success: false,
    message: '',
};

const { subscribe, set, update } = writable(initialState);

/** Same URL in flight twice → one fetch (router/location can fire duplicate updates). */
const inflightGetRequests = new Map();

export const api = {
    subscribe,
    // Normalize domain: handle undefined, empty string, or literal "undefined"/""
    domain: (() => {
        const envUrl = import.meta.env.VITE_API_URL;
        if (!envUrl || envUrl === 'undefined' || envUrl === '""' || envUrl === '') {
            return '';
        }
        return envUrl;
    })(),

    /**
     * Makes a request to the server.
     * @param {string} method - The HTTP method to use (GET, POST, PUT, DELETE).
     * @param {string} url - The endpoint URL to request.
     * @param {unknown} [body=null] - The body of the request, for POST and PUT methods.
     * @returns {Promise<ApiResponse>} The response from the server, parsed as JSON.
     */
    async makeRequest(method, url, body = null) {
        const methodUpper = String(method || 'GET').toUpperCase();

        const run = async () => {
            const token = localStorage.getItem('token') || get(auth).token;

            const isFormData = body instanceof FormData;
            const headers = {
                ...(token && { 'Authorization': `Bearer ${token}` }),
                ...(!isFormData && { 'Content-Type': 'application/json' }) // Only set Content-Type for JSON
            };

            const options = {
                method: method,
                headers: headers,
                body: isFormData ? body : body ? JSON.stringify(body) : null
            };

            let response = await fetch(this.domain + url, options);
            if (response.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('authState');
                push('/login');
            }

            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            let responseBody;

            if (response.status === 504) {
                responseBody = { success: false, message: 'The request took too long. Brief generation can take 1–2 minutes. Please try again.' };
            } else if (response.status === 502 || response.status === 503) {
                responseBody = { success: false, message: 'Server is temporarily unavailable. Please try again in a moment.' };
            } else if (!isJson) {
                const text = await response.text();
                responseBody = { success: false, message: response.ok ? 'Invalid response from server.' : `Request failed (${response.status}). Please try again.` };
            } else {
                try {
                    responseBody = await response.json();
                } catch (e) {
                    responseBody = { success: false, message: response.status === 504 ? 'The request took too long. Please try again.' : 'Invalid response from server.' };
                }
            }

            if (responseBody) {
                update(state => {
                    if (responseBody.success === false && responseBody.message === "Validation Error." && responseBody.data) {
                        const firstErrorKey = Object.keys(responseBody.data)[0];
                        state.message = responseBody.data[firstErrorKey]?.[0] || responseBody.message;
                    } else {
                        state.message = responseBody.message;
                    }
                    state.success = responseBody.success;
                    state.data = responseBody.data;
                    return state;
                });
            }
            return responseBody;
        };

        if (methodUpper === 'GET' && body == null) {
            const key = `${this.domain}\0${url}`;
            const existing = inflightGetRequests.get(key);
            if (existing) {
                return existing;
            }
            const p = run().finally(() => {
                inflightGetRequests.delete(key);
            });
            inflightGetRequests.set(key, p);
            return p;
        }

        return run();
    },

    /**
     * Encodes URL parameters from an associative array into a query string.
     * @param {HttpParams} params - The parameters to encode.
     * @returns {string} The encoded parameters as a query string.
     */
    encodeHttpParams(params) {
        return Object.keys(params)
            .filter((key) => params[key] != null && params[key] !== '')
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
            .join('&');
    },

    /**
     * Fetches the resource lists from the server.
     * @returns {Promise<boolean>} True if the request was successful, false otherwise.
     */
    async getResourceLists() {
        const body = await this.makeRequest('GET', '/api/v1/resource-lists');
        if (body.success) {
            localStorage.setItem('lists', JSON.stringify(body.data));
            update(state => {
                state.lists = body.data;
                return state;
            });
            return true;
        }
        return false;
    },

    /**
     * Initialize countries list if not already present
     */
    initializeCountries() {
        // Read from localStorage first
        const storedLists = localStorage.getItem('lists');
        const lists = storedLists ? JSON.parse(storedLists) : {};
        
        if (!lists.countries) {
            // Import countries dynamically to avoid circular dependencies
            import('./countries.js').then(({ COUNTRIES }) => {
                const countriesList = COUNTRIES.map(country => ({
                    uuid: country.code,
                    code: country.code,
                    name: country.name
                }));
                
                const updatedLists = { ...lists, countries: countriesList };
                localStorage.setItem('lists', JSON.stringify(updatedLists));
                
                // Update the store
                update(state => {
                    const currentLists = state.lists || {};
                    state.lists = { ...currentLists, countries: countriesList };
                    return state;
                });
            });
        }
    },

    /**
     * Updates a user's details on the server.
     * @param {{ uuid: string } & Record<string, unknown>} user - The new details for the user.
     * @returns {Promise<boolean>} True if the user update was successful, false otherwise.
     */
    async userUpdate(user) {
        const body = await this.makeRequest('PUT', '/api/v1/user/' + user.uuid, user);
        if (body.success) {
            update(state => {
                state.lists = body.data;
                return state;
            });
            return true;
        }
        return false;
    },

    /**
     * Fetches a list of brief type questions.
     * @param {HttpParams} [params]
     * @returns {Promise<ApiResponse>}
     */
    async xploritTypeBriefIndex(params = { column: 'sort_order' }) {
        return await this.crudIndex('xplorit-type-brief-question', params);
    },

    /**
     * Fetches a list of resources of a specific model from the server.
     * @param {string} model - The model for the index list.
     * @param {HttpParams} [params] - The HTTP parameters for the API.
     * @param {string} [extraParms]
     * @returns {Promise<ApiResponse>}
     */
    async crudIndex(model, params = {}, extraParms = '') {
        var parameters = this.encodeHttpParams(params);
        parameters = parameters ? '?' + parameters : '';
        const body = await this.makeRequest('GET', '/api/v1/' + model + parameters + extraParms);
        if (body.success) {
            return body;
        }
        return { success: false, data: [], message: body.message };
    },

    /**
     * Fetches a specific resource by its ID from the server.
     * @param {string} model - The model of the resource.
     * @param {string} id - The ID of the resource.
     * @returns {Promise<ApiResponse>} The data if the request was successful, otherwise an empty object.
     */
    async crudGet(model, id) {
        const body = await this.makeRequest('GET', '/api/v1/' + model + '/' + id);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * Updates a specific resource by its ID on the server.
     * @param {string} model - The model of the resource.
     * @param {string} id - The ID of the resource.
     * @param {FormData} putData - The new data for the resource.
     * @returns {Promise<ApiResponse>} The data if the request was successful, otherwise an empty object.
     */
    async crudUpdate(model, id, putData) {
        putData.append('_method', 'PUT');
        const body = await this.makeRequest('POST', '/api/v1/' + model + '/' + id, putData);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * Creates a new resource on the server.
     * @param {string} model - The model of the resource.
     * @param {unknown} postData - The data for the new resource.
     * @returns {Promise<ApiResponse>} The data if the request was successful, otherwise an empty object.
     */
    async crudCreate(model, postData) {
        const body = await this.makeRequest('POST', '/api/v1/' + model, postData);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * Deletes a specific resource by its ID from the server.
     * @param {string} model - The model of the resource.
     * @param {string} id - The ID of the resource.
     * @returns {Promise<ApiResponse>} The data if the request was successful, otherwise an empty object.
     */
    async crudDelete(model, id) {
        const body = await this.makeRequest('DELETE', '/api/v1/' + model + '/' + id);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * Submits a list of brief type questions.
     * @param {unknown} answers - Brief answers payload.
     * @param {string|null} [briefUuid] - Optional brief UUID.
     * @returns {Promise<ApiResponse>}
     */
    async prepareBrief(answers, briefUuid = null) {
        /** @type {{ answers: unknown; brief_uuid?: string }} */
        const data = {
            answers: answers
        };
        
        if (briefUuid) {
            data.brief_uuid = briefUuid;
        }

        const body = await this.makeRequest('POST', '/api/v1/xplorit-prepare-brief', data);
        if (body.success) {
            this.updateUserCredit();
            return body;
        }
        return body || { success: false, message: 'Failed to generate brief' };
    },

    /**
     * Queue brief AI generation; poll getAiJobStatus until completed (worker persists the brief).
     * @param {string} briefUuid
     * @param {unknown} answers
     * @returns {Promise<ApiResponse>}
     */
    async enqueueBriefGenerationAsync(briefUuid, answers) {
        const body = await this.makeRequest('POST', `/api/v1/xplorit-brief/${briefUuid}/generate-async`, {
            answers: Array.isArray(answers) ? answers : Object.values(answers || {}),
        });
        return body || { success: false, message: 'Failed to queue brief generation' };
    },

    /**
     * @param {string} jobUuid
     * @returns {Promise<ApiResponse>}
     */
    async getAiJobStatus(jobUuid) {
        const body = await this.makeRequest('GET', `/api/v1/xplorit-ai-jobs/${jobUuid}`);
        return body || { success: false };
    },

    async getAssessments(search = '', sortField = 'updated', sortDirection = 'desc') {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        params.set('sort_field', sortField);
        params.set('sort_direction', sortDirection);
        const q = params.toString();
        const body = await this.makeRequest('GET', `/api/v1/assessments${q ? '?' + q : ''}`);
        return body || { success: false };
    },

    /**
     * @param {unknown} payload
     * @returns {Promise<ApiResponse>}
     */
    async createAssessment(payload) {
        return await this.makeRequest('POST', '/api/v1/assessments', payload);
    },

    /**
     * @param {string} uuid
     * @param {unknown} payload
     * @returns {Promise<ApiResponse>}
     */
    async updateAssessment(uuid, payload) {
        return await this.makeRequest('PUT', `/api/v1/assessments/${uuid}`, payload);
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async getAssessment(uuid) {
        return await this.makeRequest('GET', `/api/v1/assessments/${uuid}`);
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async enqueueAssessmentGenerationAsync(uuid) {
        return await this.makeRequest('POST', `/api/v1/assessments/${uuid}/generate-async`, {});
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async deleteAssessment(uuid) {
        const body = await this.makeRequest('DELETE', `/api/v1/assessments/${uuid}`);
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @param {string|null} [name]
     * @returns {Promise<ApiResponse>}
     */
    async startBriefFromAssessment(uuid, name = null) {
        const body = name ? { name } : {};
        return await this.makeRequest('POST', `/api/v1/assessments/${uuid}/start-brief`, body);
    },

    /**
     * @param {string} assessmentUuid
     * @returns {Promise<ApiResponse>}
     */
    async downloadAssessmentPdf(assessmentUuid) {
        const token = localStorage.getItem('token');
        const url = this.domain + `/api/v1/assessments/${assessmentUuid}/download-pdf`;
        try {
            const response = await fetch(url, {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    Accept: 'application/pdf',
                },
            });
            if (!response.ok) throw new Error('Failed to download PDF');
            const blob = await response.blob();
            const dispo = response.headers.get('Content-Disposition');
            let fileName = `assessment-${assessmentUuid}.pdf`;
            if (dispo) {
                const m = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
                if (m) fileName = decodeURIComponent(m[1].replace(/["']/g, '').trim());
            }
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(a.href);
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false };
        }
    },

    // ==================== GapFinder ====================

    /**
     * @returns {Promise<ApiResponse>}
     */
    async getGapfinderAnalyses() {
        const body = await this.makeRequest('GET', '/api/v1/gapfinder');
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async getGapfinderAnalysis(uuid) {
        const body = await this.makeRequest('GET', `/api/v1/gapfinder/${uuid}`);
        return body || { success: false };
    },

    /**
     * @param {FormData} formData
     * @returns {Promise<ApiResponse>}
     */
    async createGapfinderFromUpload(formData) {
        const body = await this.makeRequest('POST', '/api/v1/gapfinder/upload', formData);
        return body || { success: false };
    },

    /**
     * Guest trial: validate brief text or file (no auth, not persisted).
     * @param {FormData | { content: string }} payload
     * @returns {Promise<ApiResponse>}
     */
    async validateGuestGapfinder(payload) {
        const body = await this.makeRequest('POST', '/api/v1/gapfinder/guest-validate', payload);
        return body || { success: false };
    },

    /**
     * @param {unknown} payload
     * @returns {Promise<ApiResponse>}
     */
    async createGapfinderFromText(payload) {
        const body = await this.makeRequest('POST', '/api/v1/gapfinder/text', payload);
        return body || { success: false };
    },

    /**
     * @param {unknown} payload
     * @returns {Promise<ApiResponse>}
     */
    async createGapfinderFromBrief(payload) {
        const body = await this.makeRequest('POST', '/api/v1/gapfinder/from-brief', payload);
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async enqueueGapfinderGenerationAsync(uuid) {
        const body = await this.makeRequest('POST', `/api/v1/gapfinder/${uuid}/generate-async`, {});
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async deleteGapfinderAnalysis(uuid) {
        const body = await this.makeRequest('DELETE', `/api/v1/gapfinder/${uuid}`);
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async downloadGapfinderPdf(uuid) {
        const token = localStorage.getItem('token');
        const url = this.domain + `/api/v1/gapfinder/${uuid}/download-pdf`;
        try {
            const response = await fetch(url, {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    Accept: 'application/pdf',
                },
            });
            if (!response.ok) throw new Error('Failed to download PDF');
            const blob = await response.blob();
            const dispo = response.headers.get('Content-Disposition');
            let fileName = `GapFinder-${uuid}.pdf`;
            if (dispo) {
                const m = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
                if (m) fileName = decodeURIComponent(m[1].replace(/["']/g, '').trim());
            }
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(a.href);
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false };
        }
    },

    /**
     * @param {string} uuid
     * @param {string|null} [fallbackName]
     * @returns {Promise<ApiResponse>}
     */
    async downloadGapfinderSource(uuid, fallbackName = null) {
        const token = localStorage.getItem('token');
        const url = this.domain + `/api/v1/gapfinder/${uuid}/download-source`;
        try {
            const response = await fetch(url, {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });
            if (!response.ok) throw new Error('Failed to download source');
            const blob = await response.blob();
            const dispo = response.headers.get('Content-Disposition');
            let fileName = fallbackName || `gapfinder-source-${uuid}`;
            if (dispo) {
                const m = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
                if (m) fileName = decodeURIComponent(m[1].replace(/["']/g, '').trim());
            }
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(a.href);
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false };
        }
    },

    /**
   * Update user credit.
   * @returns {Promise<ApiResponse>}
   */
  async updateUserCredit() {
    const currentState = get(auth);
    const body = await this.makeRequest('PUT', '/api/v1/user-credit-update/' + currentState.user.uuid);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * @param {string} uuid
   * @param {Record<string, unknown>} data
   * @returns {Promise<ApiResponse>}
   */
  async updateUser(uuid, data) {
    const body = await this.makeRequest('PUT', '/api/v1/user/' + uuid, data);
    if (body.success) {
      // Update local auth state if this is the current user
      const currentState = get(auth);
      if (currentState.user && currentState.user.uuid === uuid) {
        // Merge the updated data with existing user data
        // If the response includes user data, use that; otherwise merge the provided data
        const updatedUserData = body.data && typeof body.data === 'object' && !Array.isArray(body.data) 
          ? { ...currentState.user, ...body.data, ...data } 
          : { ...currentState.user, ...data };
        auth.update(state => ({
          ...state,
          user: updatedUserData
        }));
      }
      return body;
    }
    return body;
  },

  /**
   * Get a brief.
   * @param {string} id
   * @returns {Promise<ApiResponse>}
   */
  async getExistingBriefResponse(id) {
    const body = await this.makeRequest('GET', '/api/v1/xplorit-brief-summary/'+id);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get a brief type by UUID.
   * @param {string} uuid
   * @returns {Promise<ApiResponse>}
   */
  async getBriefType(uuid) {
    const body = await this.makeRequest('GET', '/api/v1/xplorit-type-brief/'+uuid);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get a brief.
   * @returns {Promise<ApiResponse>}
   */
  async getUserCredit() {
    const currentState = get(auth);
    const body = await this.makeRequest('GET', '/api/v1/xplorit-brief-credit/' + currentState.user.uuid);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

    /**
     * Get a list of brief questions.
     * @param {string|null} [typeBriefUuid] - Optional brief type UUID to filter questions by
     * @returns {Promise<ApiResponse>}
     */
    async getBriefQuestions(typeBriefUuid = null) {
        let url = '/api/v1/xplorit-brief-question';
        if (typeBriefUuid) {
            // Scope to type and load all rows (default pagination is small, e.g. 10).
            url += `?xplorit_type_brief_uuid=${encodeURIComponent(typeBriefUuid)}&all=1`;
        }
        const body = await this.makeRequest('GET', url);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * Get a list of brief questions.
     * @returns {Promise<ApiResponse>}
     */
    async getXploritBriefs(search = '', sortField = 'generated', sortDirection = 'desc') {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (sortField) params.append('sort_field', sortField);
        if (sortDirection) params.append('sort_direction', sortDirection);
        
        const queryString = params.toString();
        const url = `/api/v1/xplorit-list-briefs${queryString ? '?' + queryString : ''}`;
        
        const body = await this.makeRequest('GET', url);
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    /**
     * @param {string} name
     * @returns {Promise<ApiResponse>}
     */
    async addXploritAndBrief(name) {
        const body = await this.makeRequest('PUT', '/api/v1/xplorit-add-brief', {
            name: name
        });
        if (body.success) {
            return body;
        }
        return { success: false };
    },

    // ==================== Brief Builder (new BtoB flow) ====================

    /**
     * @param {string} [search]
     * @returns {Promise<ApiResponse>}
     */
    async listBriefBuilders(search = '') {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        const q = params.toString();
        const body = await this.makeRequest('GET', `/api/v1/brief-builder${q ? '?' + q : ''}`);
        return body || { success: false };
    },

    /**
     * @param {string} name
     * @returns {Promise<ApiResponse>}
     */
    async createBriefBuilder(name) {
        const body = await this.makeRequest('POST', '/api/v1/brief-builder', { name });
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @returns {Promise<ApiResponse>}
     */
    async getBriefBuilder(uuid) {
        const body = await this.makeRequest('GET', `/api/v1/brief-builder/${uuid}`);
        return body || { success: false };
    },

    /**
     * @param {string} uuid
     * @param {Record<string, unknown>} answers
     * @returns {Promise<ApiResponse>}
     */
    async saveBriefBuilderAnswers(uuid, answers) {
        const body = await this.makeRequest('PUT', `/api/v1/brief-builder/${uuid}/answers`, {
            answers,
        });
        return body || { success: false };
    },

    /**
     * AI mid-check read-back after the first four answers.
     * @param {string} uuid
     * @param {Record<string, unknown>} answers
     * @returns {Promise<ApiResponse>}
     */
    async midCheckBriefBuilder(uuid, answers) {
        const body = await this.makeRequest('POST', `/api/v1/brief-builder/${uuid}/mid-check`, {
            answers,
        });
        return body || { success: false };
    },

    /**
     * Stream brief generation via SSE.
     * @param {string} uuid
     * @param {Record<string, unknown>} answers
     * @param {{ onToken?: (t: string) => void, onDone?: (data?: unknown) => void, onError?: (msg: string) => void }} handlers
     */
    async streamBriefBuilder(uuid, answers, handlers = {}) {
        const token = localStorage.getItem('token');
        const url = this.domain + `/api/v1/brief-builder/${uuid}/generate-stream`;
        let response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify({ answers }),
            });
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            const msg =
                error.message.includes('Failed to fetch') || error.name === 'TypeError'
                    ? 'Connection lost while starting brief generation. Please try again.'
                    : error.message || 'Failed to start brief generation';
            handlers.onError?.(msg);
            return { success: false, message: msg };
        }

        const contentType = response.headers.get('content-type') || '';

        // Credit gate / validation may return JSON 200 instead of SSE.
        if (contentType.includes('application/json') || !response.ok) {
            let msg = 'Failed to start brief generation';
            try {
                const err = await response.json();
                msg = err.message || err.data?.message || msg;
                if (err.success === false && err.message) msg = err.message;
            } catch {
                if (!response.ok) msg = `Generation failed (${response.status})`;
            }
            handlers.onError?.(msg);
            return { success: false, message: msg };
        }

        if (!response.body) {
            const msg = 'No response stream from server. Please try again.';
            handlers.onError?.(msg);
            return { success: false, message: msg };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let sawDone = false;
        let sawError = false;
        let sawToken = false;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split('\n\n');
                buffer = parts.pop() || '';
                for (const part of parts) {
                    const lines = part.split('\n');
                    let eventType = 'message';
                    let dataLine = '';
                    for (const line of lines) {
                        if (line.startsWith('event:')) eventType = line.slice(6).trim();
                        if (line.startsWith('data:')) dataLine += line.slice(5).trim();
                    }
                    if (!dataLine) continue;
                    let payload;
                    try {
                        payload = JSON.parse(dataLine);
                    } catch {
                        payload = { text: dataLine };
                    }
                    if (eventType === 'token' || payload.type === 'token') {
                        sawToken = true;
                        handlers.onToken?.(payload.data ?? payload.text ?? '');
                    } else if (eventType === 'done' || payload.type === 'done') {
                        sawDone = true;
                        handlers.onDone?.(payload.data);
                    } else if (eventType === 'error' || payload.type === 'error') {
                        sawError = true;
                        handlers.onError?.(
                            payload.message ||
                                (typeof payload.data === 'string' ? payload.data : null) ||
                                'Generation failed'
                        );
                    }
                }
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            const msg =
                error.message.includes('network') || error.name === 'TypeError'
                    ? 'Connection interrupted while writing your brief. Please try again.'
                    : error.message || 'Connection interrupted while writing your brief.';
            handlers.onError?.(msg);
            return { success: false, message: msg };
        }

        if (!sawDone && !sawError) {
            const msg = sawToken
                ? 'Connection closed before the brief finished. Please try again.'
                : 'No response received from the server. Please try again.';
            handlers.onError?.(msg);
            return { success: false, message: msg };
        }

        return { success: true };
    },


  /**
   * Save a list of brief answer.
   * @param {string} id - Brief UUID
   * @param {string} question - Question UUID
   * @param {string} answer - Answer text
   * @param {string|null|undefined} versionUuid - Optional version UUID (for draft answers)
   * @param {string} typeBriefUuid - Optional brief type UUID (defaults to active brief type)
   * @returns {Promise<ApiResponse>}
   */
  async saveBriefAnswer(id, question, answer, versionUuid, typeBriefUuid = ACTIVE_BRIEF_TYPE_UUID) {
    /** @type {{ brief_type_answer: string; xplorit_type_brief_question_uuid: string; xplorit_brief_uuid: string; xplorit_type_brief_uuid: string; xplorit_brief_version_uuid?: string }} */
    const payload = {
      brief_type_answer: answer,
      xplorit_type_brief_question_uuid: question,
      xplorit_brief_uuid: id,
      xplorit_type_brief_uuid: typeBriefUuid
    };
    
    // If versionUuid is provided, include it (for draft answers)
    if (versionUuid !== null && versionUuid !== undefined) {
      payload.xplorit_brief_version_uuid = versionUuid;
    }
    
    const body = await this.makeRequest('PUT', '/api/v1/xplorit-brief-answer/'+id, payload);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Save a list of guest brief answer.
   * @param {string} id
   * @param {string} answer
   * @returns {Promise<ApiResponse>}
   */
  async saveGuestBriefAnswer(id, answer) {
    const body = await this.makeRequest('POST', '/api/v1/xplorit-brief-guest-answer/', {
      brief_type_answer: answer,
      xplorit_brief_uuid: id,
      xplorit_type_brief_uuid: ACTIVE_BRIEF_TYPE_UUID
    });
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get shopping cart of the user.
   * @param {string} id
   * @returns {Promise<ApiResponse>}
   */
  async getBrief(id) {
    const body = await this.makeRequest('GET', '/api/v1/xplorit/' + id);
    if (body.success) {
      update(state => {
        state.cart = body.data;
        return state;
      });
      return body;
    }
    return { success: false };
  },

  /**
   * Get shopping cart of the user.
   * @param {string} id
   * @returns {Promise<ApiResponse>}
   */
  async getBriefAnswers(id) {
    const body = await this.makeRequest('GET', '/api/v1/xplorit-answers/' + id);

    if (body) {
      return body;
    }

    return { success: false };
  },

  /**
   * @param {string} id
   * @param {string} response
   * @param {number|null} [alignmentPercent]
   * @param {unknown[]|null} [answers]
   * @returns {Promise<ApiResponse>}
   */
  async saveBriefResponse(id, response, alignmentPercent = null, answers = null) {
    /** @type {{ response_summary: string; alignment_percent: number|null; answers?: unknown[] }} */
    const payload = {
      response_summary: response,
      alignment_percent: alignmentPercent
    };
    if (answers && Array.isArray(answers)) {
      payload.answers = answers;
    }
    const body = await this.makeRequest('PUT', '/api/v1/xplorit-answers-response/' + id, payload);
    if (body.success) {
      update(state => {
        state.cart = body.data;
        return state;
      });
      return body;
    }
    return { success: false };
  },

  /**
   * Get all versions for a brief
   * @param {string} briefId
   * @returns {Promise<ApiResponse>} The versions data
   */
  async getBriefVersions(briefId) {
    const body = await this.makeRequest('GET', `/api/v1/xplorit-brief/${briefId}/versions`);
    if (body.success) {
      return body;
    }
    return { success: false, data: { versions: [], total: 0 } };
  },

  /**
   * Get current version for a brief
   * @param {string} briefId
   * @returns {Promise<ApiResponse>} The current version data
   */
  async getCurrentBriefVersion(briefId) {
    const body = await this.makeRequest('GET', `/api/v1/xplorit-brief/${briefId}/versions/current`);
    if (body.success) {
      return body;
    }
    return { success: false, data: null };
  },

  /**
   * Get a specific version for a brief
   * @param {string} briefId
   * @param {number} versionNumber
   * @returns {Promise<ApiResponse>} The version data
   */
  async getBriefVersion(briefId, versionNumber) {
    const body = await this.makeRequest('GET', `/api/v1/xplorit-brief/${briefId}/versions/${versionNumber}`);
    if (body.success) {
      return body;
    }
    return { success: false, data: null };
  },

  /**
   * @param {string} briefId
   * @param {number} versionNumber
   * @param {string} title
   * @returns {Promise<ApiResponse>}
   */
  async updateBriefVersionTitle(briefId, versionNumber, title) {
    const body = await this.makeRequest('PUT', `/api/v1/xplorit-brief/${briefId}/versions/${versionNumber}/title`, { title });
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Create a draft version for a brief
   * @param {string} briefId
   * @param {number|null} baseVersion - The version number to copy answers from
   * @returns {Promise<ApiResponse>} The draft version data
   */
  async createDraftVersion(briefId, baseVersion = null) {
    const payload = baseVersion ? { base_version: baseVersion } : {};
    const body = await this.makeRequest('POST', `/api/v1/xplorit-brief/${briefId}/versions/draft`, payload);
    if (body.success) {
      return body;
    }
    return { success: false, data: null };
  },

  /**
   * Get draft version for a brief
   * @param {string} briefId
   * @returns {Promise<ApiResponse>} The draft version data
   */
  async getDraftVersion(briefId) {
    const body = await this.makeRequest('GET', `/api/v1/xplorit-brief/${briefId}/versions/draft/status`);
    if (body.success) {
      return body;
    }
    return { success: false, data: { draft: null } };
  },

  /**
   * Delete draft version for a brief
   * @param {string} briefId
   * @returns {Promise<ApiResponse>} Success status
   */
  async deleteDraftVersion(briefId) {
    const body = await this.makeRequest('DELETE', `/api/v1/xplorit-brief/${briefId}/versions/draft`);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get draft answers for a brief (answers with draft version_uuid)
   * @param {string} briefId
   * @param {string} versionUuid
   * @returns {Promise<ApiResponse>} The draft answers
   */
  async getDraftAnswers(briefId, versionUuid) {
    const body = await this.makeRequest('GET', `/api/v1/xplorit-brief/${briefId}/versions/${versionUuid}/answers`);
    if (body.success) {
      return body;
    }
    return { success: false, data: [] };
  },

    /**
     * Get shopping cart of the user.
     * @param {string} id
     * @returns {Promise<ApiResponse>}
     */
    async getShoppingCart(id) {
        const body = await this.makeRequest('GET', '/api/v1/shopping-cart/' + id);
        if (body.success) {
            update(state => {
                state.cart = body.data;
                return state;
            });
            return body;
        }
        return { success: false };
    },

     /**
     * @param {string} id
     * @param {string} itemId
     * @param {number} qty
     * @returns {Promise<ApiResponse>}
     */
    async putShoppingCartLine(id, itemId, qty) {
        const body = await this.makeRequest('PUT', '/api/v1/shopping-cart/line/' + id, {
            item_uuid: itemId,
            quantity: qty
        });
        if (body.success) {
            update(state => {
                state.cart = body.data;
                return state;
            });
            return body;
        }
        return { success: false };
    },

     /**
     * Submit shoppingcart for payment.
     * @param {string} userId
     * @param {string} paymentMethodId
     * @param {boolean} [saveCard]
     * @returns {Promise<ApiResponse>}
     */
     async submitShoppingCart(userId, paymentMethodId, saveCard = false) {
        const body = await this.makeRequest('PUT', '/api/v1/shopping-cart/submit/' + userId, {
            payment_method_id: paymentMethodId,
            save_card: saveCard
        });
        if (body.success) {
            update(state => {
                state.cart = null;
                return state;
            });
            return body;
        }
        return { success: false };
    },

    /**
     * Get meeting availability.
     * @param {{ startTime: string; endTime: string; isAllDay: boolean; attendeeEmails: string; maxResults: number; distribution: number }} opts
     * @returns {Promise<unknown>}
     */
    async getMeetingAvailability({ startTime, endTime, isAllDay, attendeeEmails, maxResults, distribution }) {
        //const url = new URL('https://availability.pinng.xyz/availability?start_time=2024-02-02T05:00:00Z&end_time=2024-02-02T06:15:00Z&is_all_day=false&attendee_emails=amelholic%40gmail.com,eugene%40blueskylabs.com.au&max_results=7&distribution=0');

        const queryString = [
        `start_time=${startTime}`,
        `end_time=${endTime}`,
        `is_all_day=${isAllDay}`,
        `attendee_emails=${encodeURIComponent(attendeeEmails)}`,
        `max_results=${maxResults}`,
        `distribution=${distribution}`
    ].join('&');

        const baseUrl = 'https://availability.pinng.xyz/availability';
        const url = `${baseUrl}?${queryString}`;

        const options = {
            method: 'GET',
        };

        let response = await fetch(url, options);
        let body = await response.json();
        return body;
    },

    /**
   * Get a user validity.
   * @returns {Promise<ApiResponse>}
   */
  async getUserValidity() {
    const currentState = get(auth);
    const body = await this.makeRequest('GET', '/api/v1/xplorit-user-validity/' + currentState.user.uuid);
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get dashboard data.
   * @returns {Promise<ApiResponse>}
   */
  async getDashboardData() {
    const body = await this.makeRequest('GET', '/api/v1/xplorit-dashboard-stats');

    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Get brief list by status.
   * @returns {Promise<ApiResponse>}
   */
  async getBriefListByStatus() {
    const body = await this.makeRequest('GET', '/api/v1/xplorit-brief-status');

    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Request email change - sends verification code to new email
   * @param {string} userId - User UUID
   * @param {string} newEmail - New email address
   * @returns {Promise<ApiResponse>} Response with verification UUID
   */
  async requestEmailChange(userId, newEmail) {
    const data = {
      email: newEmail
    };
    const body = await this.makeRequest('POST', `/api/v1/user/request-email-change/${userId}`, data);
    return body;
  },

  /**
   * Verify email change code and update email
   * @param {string} userId - User UUID
   * @param {string} verificationCode - 6-digit verification code
   * @param {string} verificationUuid - Verification UUID from request
   * @returns {Promise<ApiResponse>} Response with updated user data
   */
  async verifyEmailChange(userId, verificationCode, verificationUuid) {
    const data = {
      verification_code: verificationCode,
      verification_uuid: verificationUuid
    };
    const body = await this.makeRequest('POST', `/api/v1/user/verify-email-change/${userId}`, data);
    return body;
  },

  /**
   * Get past briefs (research library documents)
   * @returns {Promise<ApiResponse>} Response with list of uploaded documents
   */
  async getPastBriefs() {
    const body = await this.makeRequest('GET', '/api/v1/past-brief');
    if (body.success) {
      return body;
    }
    return { success: false, data: [] };
  },

  /**
   * Upload past briefs documents
   * @param {FormData} formData - FormData containing files
   * @returns {Promise<ApiResponse>} Response with uploaded document details
   */
  async uploadPastBriefs(formData) {
    const body = await this.makeRequest('POST', '/api/v1/past-brief', formData);
    return body;
  },

  /**
   * Delete a past brief document
   * @param {string} uuid - Document UUID
   * @returns {Promise<ApiResponse>} Response confirming deletion
   */
  async deletePastBrief(uuid) {
    const body = await this.makeRequest('DELETE', `/api/v1/past-brief/${uuid}`);
    return body;
  },

  /**
   * Get download URL for a past brief document
   * @param {string} uuid - Document UUID
   * @returns {string} Download URL
   */
  getPastBriefDownloadUrl(uuid) {
    return `${this.domain}/api/v1/past-brief/${uuid}/download`;
  },

  /**
   * Get activity logs with filters (admin only)
   * @param {HttpParams} [params] - from_date, to_date, user_uuid, event_type, page, per_page
   * @returns {Promise<ApiResponse>} Response with items and paging
   */
  async getActivityLogs(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/activity-log${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * Get a single activity log by UUID (admin only)
   * @param {string} uuid - Activity log UUID
   * @returns {Promise<ApiResponse>} Activity log details
   */
  async getActivityLog(uuid) {
    return await this.makeRequest('GET', `/api/v1/activity-log/${uuid}`);
  },

  /**
   * Delete a brief
   * @param {string} uuid - Brief UUID
   * @returns {Promise<ApiResponse>} Response confirming deletion
   */
  async deleteBrief(uuid) {
    const body = await this.makeRequest('DELETE', `/api/v1/xplorit-brief/${uuid}`);
    return body;
  },

  /**
   * Update brief name (updates the associated Xplorit name)
   * @param {string} briefId - The brief UUID
   * @param {string} name - The new name
   * @returns {Promise<ApiResponse>} Success status
   */
  async updateBriefName(briefId, name) {
    const body = await this.makeRequest('PUT', `/api/v1/xplorit-brief/${briefId}/name`, {
      name: name
    });
    if (body.success) {
      return body;
    }
    return { success: false };
  },

  /**
   * Download brief as PDF
   * @param {string} briefId - The brief UUID
   * @param {number|null} versionNumber - Optional version number (null for current version)
   * @returns {Promise<void>}
   */
  async downloadBriefPdf(briefId, versionNumber = null) {
    let url = `/api/v1/xplorit-brief/${briefId}/download-pdf`;
    if (versionNumber) {
      url += `?version=${versionNumber}`;
    }
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No auth token available');
      return;
    }
    
    try {
      // Fetch PDF with auth header
      const response = await fetch(`${this.domain}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'brief.pdf'; // Default fallback
      
      if (contentDisposition) {
        // Try to extract filename from Content-Disposition header
        // Format: attachment; filename="filename.pdf" or attachment; filename*=UTF-8''filename.pdf
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes
        }
        
        // If no filename found in quoted format, try RFC 5987 format (filename*=UTF-8''...)
        if (fileName === 'brief.pdf') {
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            fileName = decodeURIComponent(filenameStarMatch[1]);
          }
        }
      }
      
      // Create blob and download
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  },

  /**
   * Submit a proposal request for a brief
   * @param {string} briefId - The brief UUID
   * @param {object} data - Proposal request data (contact_name, email, phone, notes)
   * @returns {Promise<object>} Response from the API
   */
  async submitProposalRequest(briefId, data) {
    return await this.makeRequest('POST', `/api/v1/xplorit-brief/${briefId}/proposal-request`, data);
  },

  /**
   * Payment Methods / Cards API
   * @param {string} userUuid
   * @returns {Promise<ApiResponse>}
   */
  async getUserCards(userUuid) {
    return await this.makeRequest('GET', `/api/v1/card/${userUuid}`);
  },

  /**
   * @param {string} userUuid
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async addCard(userUuid, paymentMethodId) {
    return await this.makeRequest('POST', '/api/v1/card', {
      user_uuid: userUuid,
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * @param {string} userUuid
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async removeCard(userUuid, paymentMethodId) {
    return await this.makeRequest('DELETE', `/api/v1/card/${userUuid}`, {
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * @param {string} userUuid
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async setDefaultCard(userUuid, paymentMethodId) {
    return await this.makeRequest('PUT', `/api/v1/card/${userUuid}/default`, {
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * Payment Plans API
   * @returns {Promise<ApiResponse>}
   */
  async getPaymentPlans() {
    return await this.makeRequest('GET', '/api/v1/payment-plans');
  },

  /**
   * @param {string} uuid
   * @returns {Promise<ApiResponse>}
   */
  async getPaymentPlan(uuid) {
    return await this.makeRequest('GET', `/api/v1/payment-plans/${uuid}`);
  },

  /**
   * @returns {Promise<ApiResponse>}
   */
  async getPaymentPlanPacks() {
    return await this.makeRequest('GET', '/api/v1/payment-plan-packs');
  },

  /**
   * @param {string} uuid
   * @returns {Promise<ApiResponse>}
   */
  async getPaymentPlanPack(uuid) {
    return await this.makeRequest('GET', `/api/v1/payment-plan-packs/${uuid}`);
  },

  /**
   * Teams API
   * @param {string} name
   * @returns {Promise<ApiResponse>}
   */
  async createTeam(name) {
    return await this.makeRequest('POST', `/api/v1/teams`, { name });
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async getTeam(teamUuid) {
    return await this.makeRequest('GET', `/api/v1/teams/${teamUuid}`);
  },

  /**
   * @param {string} teamUuid
   * @param {Record<string, unknown>} data
   * @returns {Promise<ApiResponse>}
   */
  async updateTeam(teamUuid, data) {
    return await this.makeRequest('PUT', `/api/v1/teams/${teamUuid}`, data);
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async getTeamMembers(teamUuid) {
    return await this.makeRequest('GET', `/api/v1/teams/${teamUuid}/members`);
  },

  /**
   * @param {string} teamUuid
   * @param {string} email
   * @returns {Promise<ApiResponse>}
   */
  async inviteTeamMember(teamUuid, email) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/invite`, { email });
  },

  /**
   * @param {string} token
   * @returns {Promise<ApiResponse>}
   */
  async getTeamInvitationInfo(token) {
    return await this.makeRequest('GET', `/api/v1/team-invitations/${token}`);
  },

  /**
   * @param {string} token
   * @returns {Promise<ApiResponse>}
   */
  async acceptTeamInvitation(token) {
    return await this.makeRequest('POST', `/api/v1/team-invitations/${token}/accept`);
  },

  /**
   * @param {string} token
   * @param {Record<string, unknown>} data
   * @returns {Promise<ApiResponse>}
   */
  async registerViaInvitation(token, data) {
    return await this.makeRequest('POST', `/api/v1/team-invitations/${token}/register`, data);
  },

  /**
   * @param {string} teamUuid
   * @param {string} userUuid
   * @returns {Promise<ApiResponse>}
   */
  async removeTeamMember(teamUuid, userUuid) {
    return await this.makeRequest('DELETE', `/api/v1/teams/${teamUuid}/members/${userUuid}`);
  },

  /**
   * @param {string} teamUuid
   * @param {string} invitationUuid
   * @returns {Promise<ApiResponse>}
   */
  async cancelTeamInvitation(teamUuid, invitationUuid) {
    return await this.makeRequest('DELETE', `/api/v1/teams/${teamUuid}/invitations/${invitationUuid}`);
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async leaveTeam(teamUuid) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/leave`);
  },

  /**
   * @param {string} teamUuid
   * @param {string} userUuid
   * @param {string} role
   * @returns {Promise<ApiResponse>}
   */
  async updateTeamMemberRole(teamUuid, userUuid, role) {
    return await this.makeRequest('PUT', `/api/v1/teams/${teamUuid}/members/${userUuid}/role`, { role });
  },

  /**
   * @param {string} userUuid
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async switchTeam(userUuid, teamUuid) {
    return await this.makeRequest('POST', `/api/v1/users/${userUuid}/switch-team`, { team_uuid: teamUuid });
  },

  /**
   * @param {string} userUuid
   * @returns {Promise<ApiResponse>}
   */
  async getUserTeams(userUuid) {
    return await this.makeRequest('GET', `/api/v1/users/${userUuid}/teams`);
  },

  /**
   * @returns {Promise<ApiResponse>}
   */
  async getDashboardStats() {
    return await this.makeRequest('GET', `/api/v1/dashboard/stats`);
  },

  /**
   * @param {number} [limit]
   * @param {number} [days]
   * @returns {Promise<ApiResponse>}
   */
  async getDashboardActivity(limit = 5, days = 90) {
    return await this.makeRequest('GET', `/api/v1/dashboard/activity?limit=${limit}&days=${days}`);
  },

  /**
   * @param {string} teamUuid
   * @param {string} paymentPlanUuid
   * @param {string} billingInterval
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async assignTeamPlan(teamUuid, paymentPlanUuid, billingInterval, paymentMethodId) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/payment-plan/assign`, {
      payment_plan_uuid: paymentPlanUuid,
      billing_interval: billingInterval,
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async getTeamUsage(teamUuid) {
    return await this.makeRequest('GET', `/api/v1/teams/${teamUuid}/payment-plan/usage`);
  },

  /**
   * @param {string} teamUuid
   * @param {string} packUuid
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async purchaseTeamPack(teamUuid, packUuid, paymentMethodId) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/payment-plan/purchase-pack`, {
      payment_plan_pack_uuid: packUuid,
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * @returns {Promise<ApiResponse>}
   */
  async getAddonServices() {
    return await this.makeRequest('GET', '/api/v1/products/addon-services');
  },

  /**
   * @param {string} teamUuid
   * @param {string} productUuid
   * @param {string} paymentMethodId
   * @returns {Promise<ApiResponse>}
   */
  async purchaseTeamAddon(teamUuid, productUuid, paymentMethodId) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/payment-plan/purchase-addon`, {
      product_uuid: productUuid,
      payment_method_id: paymentMethodId,
    });
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async cancelTeamPlan(teamUuid) {
    return await this.makeRequest('POST', `/api/v1/teams/${teamUuid}/payment-plan/cancel`);
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async canTeamCreateBrief(teamUuid) {
    return await this.makeRequest('GET', `/api/v1/teams/${teamUuid}/payment-plan/can-create-brief`);
  },

  /**
   * @param {string} userUuid
   * @returns {Promise<ApiResponse>}
   */
  async getUserInvoices(userUuid) {
    return await this.makeRequest('GET', `/api/v1/users/${userUuid}/invoices`);
  },

  /**
   * @param {string} invoiceUuid
   * @returns {Promise<ApiResponse>}
   */
  async getInvoice(invoiceUuid) {
    return await this.makeRequest('GET', `/api/v1/invoices/${invoiceUuid}`);
  },

  /**
   * Download invoice as PDF.
   * @param {string} invoiceUuid - Invoice UUID
   * @param {string|null} [invoiceNumber] - Optional invoice number for filename fallback
   * @returns {Promise<void>}
   */
  async downloadInvoicePdf(invoiceUuid, invoiceNumber = null) {
    const url = `/api/v1/invoices/${invoiceUuid}/download`;
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No auth token available');
      return;
    }
    try {
      const response = await fetch(`${this.domain}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = invoiceNumber ? `Invoice-${invoiceNumber}.pdf` : 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1].replace(/['"]/g, '');
        }
        if (fileName === 'invoice.pdf') {
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            fileName = decodeURIComponent(filenameStarMatch[1]);
          }
        }
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  },

  // Proposal Requests
  /**
   * @param {string|null} [status]
   * @param {string|null} [search]
   * @returns {Promise<ApiResponse>}
   */
  async getProposalRequests(status = null, search = null) {
    let url = '/api/v1/proposal-requests';
    const params = [];
    if (status && status !== 'all') params.push(`status=${encodeURIComponent(status)}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (params.length > 0) url += '?' + params.join('&');
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {string} uuid
   * @returns {Promise<ApiResponse>}
   */
  async getProposalRequest(uuid) {
    return await this.makeRequest('GET', `/api/v1/proposal-requests/${uuid}`);
  },

  /**
   * @param {string} uuid
   * @param {string} status
   * @returns {Promise<ApiResponse>}
   */
  async updateProposalRequestStatus(uuid, status) {
    return await this.makeRequest('PUT', `/api/v1/proposal-requests/${uuid}/status`, { status });
  },

  /**
   * @param {string} uuid
   * @returns {Promise<ApiResponse>}
   */
  async deleteProposalRequest(uuid) {
    return await this.makeRequest('DELETE', `/api/v1/proposal-requests/${uuid}`);
  },

  // Admin Teams
  /**
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminTeams(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/teams${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {string} teamUuid
   * @returns {Promise<ApiResponse>}
   */
  async getAdminTeamDetail(teamUuid) {
    return await this.makeRequest('GET', `/api/v1/admin/teams/${teamUuid}`);
  },

  /**
   * @param {string} userUuid
   * @returns {Promise<ApiResponse>}
   */
  async getAdminUserDetail(userUuid) {
    return await this.makeRequest('GET', `/api/v1/admin/users/${userUuid}`);
  },

  /**
   * @returns {Promise<ApiResponse>}
   */
  async getAdminBillingDashboard() {
    return await this.makeRequest('GET', '/api/v1/admin/billing');
  },

  // AI Generation Jobs
  /**
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAiJobs(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/xplorit-ai-jobs${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminAiJobs(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/xplorit-ai-jobs${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminCreditUsage(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/credit-usage${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {string} teamUuid
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminTeamCreditUsage(teamUuid, params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/teams/${teamUuid}/credit-usage${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {string} teamUuid
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminTeamBriefs(teamUuid, params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/teams/${teamUuid}/briefs${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {string} teamUuid
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminTeamInvoices(teamUuid, params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/teams/${teamUuid}/invoices${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /**
   * @param {HttpParams} [params]
   * @returns {Promise<ApiResponse>}
   */
  async getAdminInvoices(params = {}) {
    const queryString = this.encodeHttpParams(params);
    const url = `/api/v1/admin/invoices${queryString ? '?' + queryString : ''}`;
    return await this.makeRequest('GET', url);
  },

  /** Admin — platform knowledge */
  async getAdminPlatformKnowledge() {
    return await this.makeRequest('GET', '/api/v1/admin/knowledge/platform');
  },

  /** @param {FormData | Record<string, unknown>} formData */
  async createAdminPlatformKnowledge(formData) {
    return await this.makeRequest('POST', '/api/v1/admin/knowledge/platform', formData);
  },

  /** Admin — brief-type knowledge */
  /** @param {string} briefTypeUuid */
  async getAdminBriefTypeKnowledge(briefTypeUuid) {
    return await this.makeRequest('GET', `/api/v1/admin/knowledge/brief-types/${briefTypeUuid}`);
  },

  /** @param {string} briefTypeUuid @param {FormData | Record<string, unknown>} formData */
  async createAdminBriefTypeKnowledge(briefTypeUuid, formData) {
    return await this.makeRequest('POST', `/api/v1/admin/knowledge/brief-types/${briefTypeUuid}`, formData);
  },

  /** @param {string} uuid @param {Record<string, unknown>} data */
  async updateAdminKnowledge(uuid, data) {
    return await this.makeRequest('PUT', `/api/v1/admin/knowledge/${uuid}`, data);
  },

  /** @param {string} uuid */
  async deleteAdminKnowledge(uuid) {
    return await this.makeRequest('DELETE', `/api/v1/admin/knowledge/${uuid}`);
  },

  /** @param {string} uuid */
  async publishAdminKnowledge(uuid) {
    return await this.makeRequest('POST', `/api/v1/admin/knowledge/${uuid}/publish`);
  },

  /** @param {string} uuid */
  async archiveAdminKnowledge(uuid) {
    return await this.makeRequest('POST', `/api/v1/admin/knowledge/${uuid}/archive`);
  },

  /** @param {Record<string, unknown>} payload */
  async testAdminKnowledgeRetrieval(payload) {
    return await this.makeRequest('POST', '/api/v1/admin/knowledge/test-retrieval', payload);
  },

  /** @param {FormData | Record<string, unknown>} formData */
  async validateAdminKnowledgeContent(formData) {
    return await this.makeRequest('POST', '/api/v1/admin/knowledge/validate-content', formData);
  },

  /** Team knowledge */
  async getTeamKnowledge() {
    return await this.makeRequest('GET', '/api/v1/knowledge/team');
  },

  /** @param {FormData | Record<string, unknown>} formData */
  async createTeamKnowledge(formData) {
    return await this.makeRequest('POST', '/api/v1/knowledge/team', formData);
  },

  /** @param {string} uuid @param {Record<string, unknown>} data */
  async updateTeamKnowledge(uuid, data) {
    return await this.makeRequest('PUT', `/api/v1/knowledge/team/${uuid}`, data);
  },

  /** @param {string} uuid */
  async deleteTeamKnowledge(uuid) {
    return await this.makeRequest('DELETE', `/api/v1/knowledge/team/${uuid}`);
  },

  /** @param {FormData | Record<string, unknown>} formData */
  async validateTeamKnowledgeContent(formData) {
    return await this.makeRequest('POST', '/api/v1/knowledge/team/validate-content', formData);
  },

  /** @param {string} uuid */
  async retryTeamKnowledge(uuid) {
    return await this.makeRequest('POST', `/api/v1/knowledge/team/${uuid}/retry`);
  },

};
